import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBillingDashboardData,
  getInvoices,
  getServicesForInvoice,
  createInvoice,
  getInvoice,
  updateInvoice,
  markInvoiceAsPaid,
  sendInvoice,
  deleteInvoice,
  GetInvoicesRequest,
  GetServicesRequest,
  CreateInvoiceRequest,
  getInvoicePdf,
  bulkSendInvoices,
  getInvoicesForExport,
} from "./billing.api";

// Query Keys
export const BILLING_KEYS = {
  all: ["billing"] as const,
  lists: () => [...BILLING_KEYS.all, "list"] as const,
  list: (filters: GetInvoicesRequest) => [...BILLING_KEYS.lists(), filters] as const,
  details: () => [...BILLING_KEYS.all, "detail"] as const,
  detail: (id: string) => [...BILLING_KEYS.details(), id] as const,
  dashboard: (params?: { startDate?: string; endDate?: string }) =>
    [...BILLING_KEYS.all, "dashboard", params] as const,
  services: (params?: GetServicesRequest) =>
    [...BILLING_KEYS.all, "services", params] as const,
  pdf: (id: string) => [...BILLING_KEYS.all, "pdf", id] as const,
};

// Dashboard Query
export const useBillingDashboard = (startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: BILLING_KEYS.dashboard({ startDate, endDate }),
    queryFn: () => getBillingDashboardData(startDate, endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Invoices List Query
export const useInvoices = (params: GetInvoicesRequest = {}) => {
  return useQuery({
    queryKey: BILLING_KEYS.list(params),
    queryFn: () => getInvoices(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Single Invoice Query
export const useInvoice = (id: string) => {
  return useQuery({
    queryKey: BILLING_KEYS.detail(id),
    queryFn: () => getInvoice(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Services for Invoice Query
export const useServicesForInvoice = (params?: GetServicesRequest) => {
  return useQuery({
    queryKey: BILLING_KEYS.services(params),
    queryFn: () => getServicesForInvoice(params || {}),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Create Invoice Mutation
export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInvoiceRequest) => createInvoice(data),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.dashboard() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.services() });
    },
  });
};

// Update Invoice Mutation
export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updateInvoice(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.dashboard() });
    },
  });
};

// Mark Invoice as Paid Mutation
export const useMarkInvoiceAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      paymentMethod,
      paidAmount,
    }: {
      id: string;
      paymentMethod: string;
      paidAmount?: number;
    }) => markInvoiceAsPaid(id, paymentMethod, paidAmount),
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.dashboard() });
    },
  });
};

// Send Invoice Mutation
export const useSendInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sendInvoice(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
    },
  });
};

// Delete Invoice Mutation
export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInvoice(id),
    onSuccess: (_, id) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.dashboard() });
      // Remove from cache
      queryClient.removeQueries({ queryKey: BILLING_KEYS.detail(id) });
    },
  });
}; 

export const useGetInvoicePdf = (id: string) => {
  return useQuery({
    queryKey: BILLING_KEYS.pdf(id),
    queryFn: () => getInvoicePdf(id),
  });
};

// Bulk Send Invoices Mutation
export const useBulkSendInvoices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ids,
      exportFilename,
    }: {
      ids: string[];
      exportFilename?: string;
    }) => bulkSendInvoices(ids, exportFilename),
    onSuccess: () => {
      // Invalidate and refetch all invoice lists
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: BILLING_KEYS.dashboard() });
    },
  });
};

// Get Invoices For Export (not a hook, just re-export for convenience)
export { getInvoicesForExport };